import { dictionaries, type Dictionary, type Locale, type PluralForms } from "./dictionary";

export type { Dictionary, Locale, PluralForms };
export { dictionaries };

export type Direction = "rtl" | "ltr";

export const LOCALES = ["ar", "en"] as const satisfies readonly Locale[];

/** Arabic is the primary language of the product, not a translation of English. */
export const DEFAULT_LOCALE: Locale = "ar";

export const LOCALE_NATIVE_NAMES: Record<Locale, string> = {
  ar: "العربية",
  en: "English",
};

/**
 * Latin digits in Arabic: Gulf/Levant business documents and the ERP's own
 * numeric data are written with them, and mixing numeral systems across
 * screens reads as a bug to accountants.
 */
const INTL_TAGS: Record<Locale, string> = {
  ar: "ar-u-nu-latn",
  en: "en",
};

export function isLocale(value: unknown): value is Locale {
  return value === "ar" || value === "en";
}

export function getDirection(locale: Locale): Direction {
  return locale === "ar" ? "rtl" : "ltr";
}

export function getDictionary(locale: Locale): Dictionary {
  return dictionaries[locale];
}

export function getOppositeLocale(locale: Locale): Locale {
  return locale === "ar" ? "en" : "ar";
}

/** Maps the backend `User.preferredLanguage` enum onto a locale. */
export function localeFromPreferredLanguage(preferredLanguage: number): Locale {
  return preferredLanguage === 2 ? "ar" : "en";
}

const numberFormatters = new Map<Locale, Intl.NumberFormat>();
const pluralRules = new Map<Locale, Intl.PluralRules>();

export function formatNumber(locale: Locale, value: number): string {
  let formatter = numberFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(INTL_TAGS[locale]);
    numberFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

/**
 * Resolves a CLDR plural form and substitutes `{count}`. Arabic distinguishes
 * six categories, so a naive `count === 1 ? singular : plural` is wrong here.
 */
export function plural(locale: Locale, forms: PluralForms, count: number): string {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(INTL_TAGS[locale]);
    pluralRules.set(locale, rules);
  }

  // English maps 0 to `other` ("0 reports"); prefer an explicit zero phrasing.
  const category = count === 0 && forms.zero !== undefined ? "zero" : rules.select(count);
  const template = forms[category] ?? forms.other;
  return template.replace("{count}", formatNumber(locale, count));
}
